import * as React from 'react';
import { DatePicker as DatePickerStandard } from '@emorg-prd/standard-react';
import { Field } from './Field';
import { IValidation } from '../../../utils/Validation';
import './DatePickerField.css';

interface DatePickerFieldProps
	extends Omit<React.ComponentProps<typeof DatePickerStandard>, 'state' | 'id' | 'initialValue' | 'showInitialValue'> {
	id?: string;
	value?: string;
	onChange?: (...args: any[]) => any;
	validation?: IValidation;
	className?: string;
	style?: React.CSSProperties;
}

const toIsoDate = (value?: string): string | undefined => {
	if (!value) return undefined;
	const parsed = new Date(value);
	return isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
};

const DatePickerField: React.FC<DatePickerFieldProps> = (props) => {
	const { id, value, onChange, validation, className, ...rest } = props;
	const generatedId = React.useId();
	const elementId = id || `date-picker-${generatedId}`;
	const isoValue = toIsoDate(value);

	const abstractDatePicker = new (class extends Field<DatePickerFieldProps> {
		constructor() {
			super({ onChange, value, validation });
		}
	})();

	React.useEffect(() => {
		if (!onChange) return;
		const element = document.getElementById(elementId);
		if (!element) return;

		const handleChange = (event: Event) => {
			const selectedDate = 'detail' in event ? (event as CustomEvent<string>).detail : (event.target as HTMLInputElement).value;
			onChange({ target: { value: selectedDate } });
		};

		element.addEventListener('change', handleChange);
		element.addEventListener('selectDate', handleChange);
		return () => {
			element.removeEventListener('change', handleChange);
			element.removeEventListener('selectDate', handleChange);
		};
	}, [elementId, onChange]);

	const optionalClass = ['custom-datepicker', className].filter(Boolean).join(' ');

	return (
		<DatePickerStandard
			{...rest}
			id={elementId}
			initialValue={isoValue}
			showInitialValue={!!isoValue}
			state={validation ? (abstractDatePicker.isValid() ? 'valid' : 'error') : undefined}
			optionalClass={optionalClass}
			note={abstractDatePicker.nonValidMessage() || props.note}
		/>
	);
};

export default DatePickerField;
